import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { seguridadGuard } from '@core/guards/seguridad/seguridad.guard';
import { algunPermisoGuard, permisoGuard } from '@core/guards/permiso.guard';
import { flujoPostLoginGuard } from '@core/guards/flujo-post-login.guard';
import { DefaultLayoutComponent } from '@core/layouts/default-layout/default-layout.component';
import { NavBarLayoutComponent } from '@core/layouts/navbar-layout/navbar-layout.component';
import { accesoBusquedaClienteGuard, accesoOrganizacionPropiaGuard } from '@gestion-cliente/guards/acceso-gestion-cliente.guard';
import { cambiosSinGuardarGuard } from '@gestion-cliente/guards/cambios-sin-guardar.guard';
import { ALCANCE_FICHA } from '@gestion-cliente/models/alcance-ficha.enum';
import { PESTANA_FICHA } from '@gestion-cliente/models/pestana-ficha.enum';
import { BREADCRUMBS_MAPEO_CAMPOS, CLAVE_TIPO_ARCHIVO_MAPEO, PESTANAS_MAPEO_CAMPOS } from '@mapeo-campos/constants/mapeo-campos.constants';
import { PESTANAS_CARGA_MASIVA } from '@carga-masiva/constants/pestanas-carga-masiva.constants';
import { redirigirAPrimeraPestanaPermitida } from '@shared/componentes/pestanas-navegacion/pestanas-navegacion.helper';
import { TIPO_ARCHIVO_MAPEO } from '@mapeo-campos/enums/tipo-archivo-mapeo.enum';
import { PERMISOS_CONFIGURACION_OPCIONES, PERMISOS_CARGA_MASIVA, PERMISOS_CONFIRMING, PERMISOS_GESTION_CLIENTE, PERMISOS_GRUPOS_PERMISOS, PERMISOS_MAPEO_CAMPOS, PERMISOS_PLANTILLAS, PERMISOS_PERFECCIONAMIENTO, PERMISOS_PROCESO_NOTIFICACION, PERMISOS_PROCESO_NOTIFICACION_VENTA, PERMISOS_VENTA_FACTURAS } from '@shared/constants/permisos';

export const routes: Routes = [
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivate: [MsalGuard, flujoPostLoginGuard],
    children: [
      {
        path: '',
        data: {
          breadcrumb: {
            label: 'Inicio',
            info: { icon: 'home' }
          } 
        },
        loadComponent: () => import('@inicio/components/inicio/inicio.component').then(m => m.InicioComponent)
      },
      {
        path: 'radicaciones',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Radicar negocio' },
        children: [
          {
            path: 'factura',
            data: { breadcrumb: 'Cargar facturas' },
            loadComponent: () => import(
              '@radicaciones/crear-radicacion-factura/components/crear-radicacion-factura.component'
            ).then(m => m.RadicacionFacturaComponent),
            canActivate: [permisoGuard('RadicacionFactura.Crear')],
            canMatch: [seguridadGuard]
          },
          {
            path: 'instruccion-giro',
            data: { 
              breadcrumb: 'Cargar instrucciones de giro'
            },
            loadComponent: () => import(
              '@radicaciones/crear-radicacion-instruccion-giro/componentes/crear-radicacion-instruccion-giro.component'
            ).then(m => m.CrearRadicacionInstruccionGiroComponent),
            canActivate: [permisoGuard('RadicacionInstruccionesGiro.Crear')],
            canMatch: [seguridadGuard]
          },
          {
            path: 'revision-instruccion-giro',
            data: { 
              breadcrumb: 'Revisión instrucciones de giro'
            },
            loadComponent: () => import(
              '@radicaciones/revision-confirmacion-instruccion-giro/componentes/revision-confirmacion-instruccion-giro.component'
            ).then(m => m.RevisionConfirmacionInstruccionGiroComponent),
            canActivate: [permisoGuard('RadicacionInstruccionesGiro.Ver')],
            canMatch: [seguridadGuard]
          }
        ]
      },
      {
        path: 'gestion-operativa',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Gestión operativa' },
        children: [
          {
            path: 'sarlaft-manual',
            data: { breadcrumb: 'Sarlaft manual' },
            loadComponent: () => import(
              '@instrucciones-giro/sarlaft-manual/components/sarlaft-manual.component'
            ).then(m => m.SarlaftManualComponent),
            canActivate: [permisoGuard('SarlafManual.Ver')],
            canMatch: [seguridadGuard]
          }
        ]
      },
      {
        path: 'facturas',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Facturas' },
        children: [
          {
            path: 'validar-factura',
            data: { breadcrumb: 'Validar factura' },
            loadComponent: () => import(
              '@facturas/validar-factura/components/validar-factura.component'
            ).then(m => m.ValidarFacturaComponent),
            canActivate: [permisoGuard('RadicacionFactura.Validar')],
            canMatch: [seguridadGuard]
          },
          {
            path: 'notificar',
            data: { breadcrumb: 'Notificar facturas' },
            loadComponent: () => import(
              './feature/facturas/notificar-facturas/components/listado-facturas-emisor/listado-facturas-emisor.component'
            ).then(m => m.ListadoFacturasEmisorComponent),
            canActivate: [permisoGuard('Notificaciones.Ver')],
            canMatch: [seguridadGuard]
          },
          {
            path: 'gestion-factor',
            data: { breadcrumb: 'Facturas en proceso' },
            loadComponent: () => import(
              './feature/facturas/gestion-facturas-factor/components/listado-facturas-factor/listado-facturas-factor.component'
            ).then(m => m.ListadoFacturasFactorComponent),
            canActivate: [permisoGuard(PERMISOS_PERFECCIONAMIENTO.VER)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'proceso-notificacion',
            data: { breadcrumb: 'Proceso de notificación' },
            loadComponent: () => import(
              './feature/facturas/proceso-notificacion-factor/components/listado-notificaciones-factor/listado-notificaciones-factor.component'
            ).then(m => m.ListadoNotificacionesFactorComponent),
            canActivate: [permisoGuard(PERMISOS_PROCESO_NOTIFICACION.VER)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'proceso-notificacion-emisor',
            data: { breadcrumb: 'Proceso de notificación' },
            loadComponent: () => import(
              './feature/facturas/proceso-notificacion-emisor/components/listado-notificaciones-emisor/listado-notificaciones-emisor.component'
            ).then(m => m.ListadoNotificacionesEmisorComponent),
            canActivate: [permisoGuard(PERMISOS_PROCESO_NOTIFICACION.VER_EMISOR)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'proceso-notificacion-venta',
            data: { breadcrumb: 'Notificación de la Venta' },
            loadComponent: () => import(
              './feature/facturas/proceso-notificacion-venta/components/listado-notificaciones-venta/listado-notificaciones-venta.component'
            ).then(m => m.ListadoNotificacionesVentaComponent),
            canActivate: [permisoGuard(PERMISOS_PROCESO_NOTIFICACION_VENTA.VER)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'fondeador',
            data: {
              parentBreadcrumb: 'Venta de facturas',
              breadcrumb: 'Asignación a fondeador'
            },
            loadComponent: () => import(
              './feature/facturas/venta-facturas/components/listado-venta-facturas/listado-venta-facturas.component'
            ).then(m => m.ListadoVentaFacturasComponent),
            canActivate: [permisoGuard(PERMISOS_VENTA_FACTURAS.VER)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'aprobacion-fondeador',
            data: { breadcrumb: 'Aprobación de ventas' },
            loadComponent: () => import(
              './feature/facturas/venta-facturas/components/listado-aprobacion-venta-fondeador/listado-aprobacion-venta-fondeador.component'
            ).then(m => m.ListadoAprobacionVentaFondeadorComponent),
            canActivate: [permisoGuard(PERMISOS_VENTA_FACTURAS.APROBAR)],
            canMatch: [seguridadGuard]
          }
        ]
      },
      {
        path: 'plantillas',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Gestión de plantillas' },
        children: [
          {
            path: '',
            data: { breadcrumb: 'Listado' },
            loadComponent: () => import(
              './feature/gestion-plantillas/pages/gestion-plantillas-page/gestion-plantillas-page.component'
            ).then(m => m.GestionPlantillasPageComponent)
          },
          {
            path: 'tipos',
            redirectTo: '',
            pathMatch: 'full'
          },
          {
            path: 'nueva',
            data: { breadcrumb: 'Nueva plantilla' },
            loadComponent: () => import(
              './feature/gestion-plantillas/components/editor-plantilla/editor-plantilla.component'
            ).then(m => m.EditorPlantillaComponent),
            canActivate: [permisoGuard(PERMISOS_PLANTILLAS.ADMINISTRAR_PLANTILLA)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'aprobar',
            data: { breadcrumb: 'Solicitudes de aprobación' },
            loadComponent: () => import(
              './feature/solicitudes-aprobacion/pages/solicitudes-aprobacion-page/solicitudes-aprobacion-page.component'
            ).then(m => m.SolicitudesAprobacionPageComponent),
            canActivate: [permisoGuard(PERMISOS_PLANTILLAS.APROBAR_PLANTILLA)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'aprobar/:id',
            data: { breadcrumb: 'Aprobación de plantillas' },
            loadComponent: () => import(
              './feature/gestion-plantillas/components/aprobar-plantilla/aprobar-plantilla.component'
            ).then(m => m.AprobarPlantillaComponent),
            canActivate: [permisoGuard(PERMISOS_PLANTILLAS.APROBAR_PLANTILLA)],
            canMatch: [seguridadGuard]
          },
          {
            path: ':id/editar',
            data: { breadcrumb: 'Editar plantilla' },
            loadComponent: () => import(
              './feature/gestion-plantillas/components/editor-plantilla/editor-plantilla.component'
            ).then(m => m.EditorPlantillaComponent),
            canActivate: [permisoGuard(PERMISOS_PLANTILLAS.ADMINISTRAR_PLANTILLA)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'editor/:tipoDocumentoId',
            data: { breadcrumb: 'Editor de plantillas' },
            loadComponent: () => import(
              './feature/gestion-plantillas/components/editor-plantilla/editor-plantilla.component'
            ).then(m => m.EditorPlantillaComponent),
            canActivate: [permisoGuard(PERMISOS_PLANTILLAS.ADMINISTRAR_PLANTILLA)],
            canMatch: [seguridadGuard]
          }
        ]
      },
      {
        path: 'configuracion',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Configuración' },
        children: [
          {
            path: 'gestion-cliente',
            data: { breadcrumb: 'Gestión de cliente' },
            children: [
              {
                path: '',
                loadComponent: () => import(
                  '@gestion-cliente/components/busqueda-cliente/busqueda-cliente.component'
                ).then(m => m.BusquedaClienteComponent),
                canActivate: [accesoBusquedaClienteGuard]
              },
              {
                path: 'mi-organizacion',
                data: { breadcrumb: 'Mi organización', alcance: ALCANCE_FICHA.PROPIA },
                loadComponent: () => import(
                  '@gestion-cliente/components/ficha-cliente/ficha-cliente.component'
                ).then(m => m.FichaClienteComponent),
                canActivate: [accesoOrganizacionPropiaGuard],
                canDeactivate: [cambiosSinGuardarGuard]
              },
              {
                path: ':clienteId',
                data: { breadcrumb: 'Ficha del cliente', alcance: ALCANCE_FICHA.TODOS },
                loadComponent: () => import(
                  '@gestion-cliente/components/ficha-cliente/ficha-cliente.component'
                ).then(m => m.FichaClienteComponent),
                canActivate: [permisoGuard(PERMISOS_GESTION_CLIENTE.GESTIONAR_TODOS)],
                canDeactivate: [cambiosSinGuardarGuard]
              }
            ]
          },
          {
            path: 'gestionar-fondeador',
            data: {
              breadcrumb: 'Gestionar el Fondeador',
              alcance: ALCANCE_FICHA.PROPIA,
              pestanaInicial: PESTANA_FICHA.FONDEADOR
            },
            loadComponent: () => import(
              '@gestion-cliente/components/ficha-cliente/ficha-cliente.component'
            ).then(m => m.FichaClienteComponent),
            canActivate: [permisoGuard(PERMISOS_GESTION_CLIENTE.GESTIONAR_FONDEADOR)],
            canDeactivate: [cambiosSinGuardarGuard]
          }
        ]
      },
      {
        path: 'confirming',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.CONFIRMING },
        children: [
          {
            path: 'mapeo-campos',
            data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.MAPEO_CAMPOS },
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: redirigirAPrimeraPestanaPermitida(PESTANAS_MAPEO_CAMPOS)
              },
              {
                path: 'facturas',
                data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.FACTURAS, [CLAVE_TIPO_ARCHIVO_MAPEO]: TIPO_ARCHIVO_MAPEO.FACTURAS },
                loadComponent: () => import(
                  '@mapeo-campos/components/mapeo-campos/mapeo-campos.component'
                ).then(m => m.MapeoCamposComponent),
                canActivate: [permisoGuard(PERMISOS_MAPEO_CAMPOS.GESTIONAR_FACTURAS)],
                canMatch: [seguridadGuard]
              },
              {
                path: 'proveedores',
                data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.PROVEEDORES, [CLAVE_TIPO_ARCHIVO_MAPEO]: TIPO_ARCHIVO_MAPEO.PROVEEDORES },
                loadComponent: () => import(
                  '@mapeo-campos/components/mapeo-campos/mapeo-campos.component'
                ).then(m => m.MapeoCamposComponent),
                canActivate: [permisoGuard(PERMISOS_MAPEO_CAMPOS.GESTIONAR_PROVEEDORES)],
                canMatch: [seguridadGuard]
              },
              {
                path: 'informacion-bancaria',
                data: {
                  breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.INFORMACION_BANCARIA,
                  [CLAVE_TIPO_ARCHIVO_MAPEO]: TIPO_ARCHIVO_MAPEO.INFORMACION_BANCARIA
                },
                loadComponent: () => import(
                  '@mapeo-campos/components/mapeo-campos/mapeo-campos.component'
                ).then(m => m.MapeoCamposComponent),
                canActivate: [permisoGuard(PERMISOS_MAPEO_CAMPOS.GESTIONAR_INFORMACION_BANCARIA)],
                canMatch: [seguridadGuard]
              }
            ]
          },
          {
            path: 'carga-masiva',
            data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.CARGA_MASIVA },
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: redirigirAPrimeraPestanaPermitida(PESTANAS_CARGA_MASIVA)
              },
              {
                path: 'facturas',
                data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.FACTURAS },
                loadComponent: () => import(
                  '@carga-facturas/components/carga-facturas/carga-facturas.component'
                ).then(modulo => modulo.CargaFacturasComponent),
                canActivate: [permisoGuard(PERMISOS_CONFIRMING.PREPARAR)],
                canMatch: [seguridadGuard]
              },
              {
                path: 'proveedores',
                data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.PROVEEDORES },
                loadComponent: () => import(
                  '@carga-proveedores/components/carga-proveedores/carga-proveedores.component'
                ).then(modulo => modulo.CargaProveedoresComponent),
                canActivate: [algunPermisoGuard(PERMISOS_CARGA_MASIVA)],
                canMatch: [seguridadGuard]
              },
              {
                path: 'informacion-bancaria',
                data: { breadcrumb: BREADCRUMBS_MAPEO_CAMPOS.INFORMACION_BANCARIA },
                loadComponent: () => import(
                  '@carga-informacion-bancaria/components/carga-informacion-bancaria/carga-informacion-bancaria.component'
                ).then(modulo => modulo.CargaInformacionBancariaComponent),
                canActivate: [algunPermisoGuard(PERMISOS_CARGA_MASIVA)],
                canMatch: [seguridadGuard]
              }
            ]
          },
          {
            path: 'carga-informacion-bancaria',
            pathMatch: 'full',
            redirectTo: 'carga-masiva/informacion-bancaria'
          }
        ]
      },
      {
        path: 'administracion',
        canActivate: [MsalGuard, seguridadGuard],
        data: { breadcrumb: 'Administrar usuarios' },
        children: [
          {
            path: 'configuracion-opciones',
            data: { breadcrumb: 'Configuración de opciones' },
            loadComponent: () => import(
              '@configuracion-opciones/components/listado-opciones/listado-opciones.component'
            ).then(m => m.ListadoOpcionesComponent),
            canActivate: [permisoGuard(PERMISOS_CONFIGURACION_OPCIONES.GESTIONAR)],
            canMatch: [seguridadGuard]
          },
          {
            path: 'grupos-permisos',
            data: { breadcrumb: 'Perfilamiento de usuarios' },
            loadComponent: () => import(
              '@grupos-permisos/components/listado-grupos-permisos/listado-grupos-permisos.component'
            ).then(m => m.ListadoGruposPermisosComponent),
            canActivate: [permisoGuard(PERMISOS_GRUPOS_PERMISOS.GESTIONAR)],
            canMatch: [seguridadGuard]
          }
        ]
      },
      {
        path: 'gestion-usuarios',
        canActivate: [MsalGuard, permisoGuard('Administrador.GestionUsuarios')],
        canMatch: [seguridadGuard],
        data: { breadcrumb: 'Gestión de Usuarios' },
        loadComponent: () => import(
          './feature/gestion-usuarios/components/gestion-usuarios/gestion-usuarios.component'
        ).then(m => m.GestionUsuariosComponent)
      }
    ]
  },
  
  {
    path: 'seleccion-modo',
    component: NavBarLayoutComponent,
    canActivate: [MsalGuard],
    children: [
      {
        path: '',
        loadComponent: () => 
          import('./feature/seleccion-modo/components/seleccion-modo/seleccion-modo.component')
            .then(m => m.SeleccionModoComponent)
      }
    ]
  },

  {
    path: 'perfiles-empresas',
    component: NavBarLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () => 
          import('@perfiles-empresas/perfiles-empresas.routes').then(m => m.perfilesEmpresasRoutes)
      }
    ]
  },

  {
    path: 'acceso-denegado',
    loadComponent: () => import('./core/componentes/acceso-denegado/acceso-denegado.component')
      .then(m => m.AccesoDenegadoComponent)
  }
];
